import {
  BitkitControlButton,
  IconArrowDown,
  IconArrowUp,
  IconBoxLinesOverflow,
  IconBoxLinesWrap,
  IconDownload,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Group } from '@chakra-ui/react/group';
import { PropsWithChildren, RefObject, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';

type ToolbarProps = {
  isDisabled?: boolean;
  offset?: 'platform-header' | 'none';
  scrollTargetRef?: RefObject<HTMLElement | null>;
  downloadUrl?: string;
  wrap: boolean;
  onWrapChanged: (wrap: boolean) => void;
  onScrollNavigate?: (direction: 'up' | 'down') => void;
};

const SCROLL_PADDING = 16;

const LogViewerToolbar = ({
  wrap,
  isDisabled,
  scrollTargetRef,
  downloadUrl,
  offset = 'platform-header',
  onWrapChanged,
  onScrollNavigate,
  children,
}: PropsWithChildren<ToolbarProps>) => {
  const [toolbarOffsetTop, setToolbarOffsetTop] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const toggleLineWrapping = () => {
    onWrapChanged(!wrap);
  };

  useEventListener('scroll', () => {
    if (offset === 'none') {
      return;
    }

    if (toolbarRef.current) {
      const platformHeaderHeight = document.querySelector('.platform-header')?.clientHeight || 0;
      const stickyHeaderHeight = document.querySelector('.sticky-page-header')?.clientHeight || 0;
      setToolbarOffsetTop(platformHeaderHeight + stickyHeaderHeight);
    }
  });

  const handleScrollTop = () => {
    if (toolbarRef.current?.parentElement) {
      const topRefElement = scrollTargetRef?.current ?? toolbarRef.current.closest('.log-viewer');
      const topRefElementTop = topRefElement?.getBoundingClientRect().top ?? 0;
      const scrollTop = window.scrollY + topRefElementTop;
      const top = scrollTop - toolbarOffsetTop - SCROLL_PADDING;
      window.scrollTo({ top, behavior: 'smooth' });
      onScrollNavigate?.('up');
    }
  };

  const handleScrollBottom = () => {
    if (toolbarRef.current?.parentElement) {
      const bottomRefElement = toolbarRef.current.closest('.log-viewer');
      const bottomRefBottom = bottomRefElement?.getBoundingClientRect().bottom ?? 0;
      const scrollBottom = window.scrollY + bottomRefBottom;
      const top = scrollBottom - window.innerHeight + SCROLL_PADDING;
      window.scrollTo({ top, behavior: 'smooth' });
      onScrollNavigate?.('down');
    }
  };

  return (
    <Box
      ref={toolbarRef}
      position="sticky"
      top={toolbarOffsetTop}
      zIndex="1"
      gap="8"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      paddingY="4"
      paddingX="16"
      backgroundColor="background/secondary"
    >
      <Box flexGrow="1">{children}</Box>
      <Group gap="0">
        {downloadUrl && <BitkitControlButton size="xs" icon={IconDownload} label="Download" href={downloadUrl} />}
        <BitkitControlButton
          size="xs"
          onClick={toggleLineWrapping}
          label={wrap ? "Don't wrap lines" : 'Wrap lines'}
          icon={wrap ? IconBoxLinesOverflow : IconBoxLinesWrap}
          state={isDisabled ? 'disabled' : undefined}
        />
        <BitkitControlButton
          size="xs"
          icon={IconArrowUp}
          label="Scroll to Top"
          onClick={handleScrollTop}
          state={isDisabled ? 'disabled' : undefined}
        />
        <BitkitControlButton
          size="xs"
          icon={IconArrowDown}
          label="Scroll to Bottom"
          onClick={handleScrollBottom}
          state={isDisabled ? 'disabled' : undefined}
        />
      </Group>
    </Box>
  );
};

export default LogViewerToolbar;
